/**
 * Well Testing helpers (fieldiq-api#1241, WT-1): who may press what, how a
 * refusal reads, the duplicate-well 409, and which identifier the tenant's
 * country uses.
 *
 * Nothing here converts a unit or computes a figure the API returns.
 */
import type { WtMeta, WtQuantity } from '../types/well-testing';
import { formatCalendarDate } from './calendar-date';
import { fmtQty } from './display-labels';

// ─── Who may press what (mirrors fieldiq-api lib/wt-roles.ts) ────────────────
//
// For hiding a button, not for enforcement. The API's requireRole is a plain
// membership test, so a button these refuse is left off; a 403 that still
// arrives is shown as a refusal, never as success.

export const WT_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'field_worker', 'logistics'] as const;
export const WT_ADMIN_ROLES = ['admin', 'office_staff'] as const;
export const WT_WELL_WRITE_ROLES = ['admin', 'office_staff', 'supervisor'] as const;

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);
/** Tenant settings, customer settings, and deleting a well. */
export const canAdminWt = inList(WT_ADMIN_ROLES);
/** Creating and editing a well — the office plus the supervisor. */
export const canWriteWells = inList(WT_WELL_WRITE_ROLES);

// ─── Refusals ────────────────────────────────────────────────────────────────

export interface WtApiError {
  status: number | null;
  code: string | null;
  message: string | null;
  field: string | null;
  existingWellId: string | null;
}

export function wtApiError(err: unknown): WtApiError {
  const r = (err as {
    response?: { status?: unknown; data?: { error?: unknown; code?: unknown; field?: unknown; existingWellId?: unknown } };
  })?.response;
  const d = r?.data;
  return {
    status: typeof r?.status === 'number' ? r.status : null,
    code: typeof d?.code === 'string' ? d.code : null,
    message: typeof d?.error === 'string' && d.error ? d.error : null,
    field: typeof d?.field === 'string' ? d.field : null,
    existingWellId: typeof d?.existingWellId === 'string' ? d.existingWellId : null,
  };
}

/**
 * A write's error as one sentence. A 403 is said as a refusal whatever the
 * body says, so it can never read as a validation slip or a success. The API's
 * own `error` string otherwise, then the caller's fallback.
 */
export function wtErrorMessage(err: unknown, fallback: string): string {
  const e = wtApiError(err);
  if (e.status === 403) return 'You do not have permission to do this. Ask an admin or office staff.';
  return e.message ?? fallback;
}

/**
 * The 409 duplicate_well: the API's sentence names the existing well
 * ("Well "X" already has this UWI."), and `existingWellId` lets the form link
 * to it. Null when the error is anything else.
 */
export function duplicateWell(err: unknown): { message: string; existingWellId: string | null } | null {
  const e = wtApiError(err);
  if (e.status !== 409 || e.code !== 'duplicate_well') return null;
  const what = e.field === 'api_number' ? 'API number' : 'UWI';
  return {
    message: e.message ?? `Another well already has this ${what}.`,
    existingWellId: e.existingWellId,
  };
}

// ─── Identifier by country ───────────────────────────────────────────────────

/**
 * A US tenant's wells are identified by API number; every other tenant's by
 * UWI. Unknown country (meta not loaded) reads as Canada, the same fallback
 * the province picker uses.
 */
export const wtIsUs = (meta: Pick<WtMeta, 'country'> | null | undefined) => meta?.country === 'US';
export const wellIdLabel = (meta: Pick<WtMeta, 'country'> | null | undefined) => (wtIsUs(meta) ? 'API number' : 'UWI');
export const wellIdOf = (
  well: { uwi: string | null; apiNumber: string | null },
  meta: Pick<WtMeta, 'country'> | null | undefined,
): string | null => (wtIsUs(meta) ? well.apiNumber : well.uwi);

// ─── Display ─────────────────────────────────────────────────────────────────

/** A number as given, grouped; absent → `—`. A real 0 is `0`. */
export const fmtWtNumber = (n: number | null | undefined): string => fmtQty(n, 6);

/** `120 ppm`; absent → `—` with no unit after it. */
export function withUnit(n: number | null | undefined, unit: string): string {
  const v = fmtWtNumber(n);
  return v === '—' ? v : `${v} ${unit}`;
}

/** The label the API's catalog gives a value; the raw value if the catalog lacks it; `—` when absent. */
export function catalogLabel(list: readonly { value: string; label: string }[] | undefined, value: string | null | undefined): string {
  if (!value) return '—';
  return list?.find((e) => e.value === value)?.label ?? value;
}

/** An input's text as a write value: blank → null, otherwise the trimmed text. */
export const blankToNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());

/** A read value back into an input. */
export const toInput = (v: number | string | null | undefined): string => (v === null || v === undefined ? '' : String(v));

/**
 * A settings number field's text as a write value. Blank → null (not set, the
 * flag is OFF), NEVER 0. Anything that is not a plain number is an error
 * sentence naming the field; the API still does the range check.
 */
export function settingsNumber(text: string, label: string, opts: { integer?: boolean } = {}): { value: number | null } | { error: string } {
  const t = text.trim();
  if (t === '') return { value: null };
  if (!/^-?\d+(\.\d+)?$/.test(t)) return { error: `${label} must be a number${opts.integer ? ' of whole minutes' : ''}, or blank.` };
  const n = Number(t);
  if (opts.integer && !Number.isInteger(n)) return { error: `${label} must be a whole number, or blank.` };
  return { value: n };
}

// ═══ WT-2 (fieldiq-api#1245): jobs, crew, packages, rent-ins, shift logs ════


/** Writes on jobs, crew, packages, specs, equipment and rent-ins; deleting a shift log. */
export const WT_DISPATCH_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;
/** Writing a shift log — the dispatch roles plus the field hand on the crew. */
export const WT_WORK_ROLES = [...WT_DISPATCH_ROLES, 'field_worker'] as const;
/** The roles the API sends rent-in cost (rate, billable, PO) to. A field hand never gets it. */
export const WT_RENT_IN_COST_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'logistics'] as const;

export const canDispatchWt = inList(WT_DISPATCH_ROLES);
export const canWorkWt = inList(WT_WORK_ROLES);
export const canSeeWtRentInCost = inList(WT_RENT_IN_COST_ROLES);

/** A calendar day as 'Oct 26, 2026'; never through `new Date('YYYY-MM-DD')`. Absent → `—`. */
export const wtDay = (value: string | null | undefined): string => formatCalendarDate(value) ?? '—';

/**
 * A quantity as the hand typed it, with its unit: `9,930 kPa`, `1,440 psi`.
 * Never the metric copy — that is for totals. Absent → `—`.
 */
export function wtQuantity(q: WtQuantity | null | undefined): string {
  if (!q || !q.entered || q.entered.value === null || q.entered.value === undefined || q.entered.value === '') return '—';
  const n = Number(q.entered.value);
  const v = Number.isFinite(n) ? fmtWtNumber(n) : String(q.entered.value);
  return q.entered.unit ? `${v} ${q.entered.unit}` : v;
}

export interface WtSkin {
  label: string;
  fill: string;
  rail: string;
}
export const WT_UNKNOWN_SKIN: WtSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

/**
 * One hue per meaning: draft steel, scheduled blue, the three on-site stages
 * amber (work is running), completed green, cancelled red.
 */
export const WT_JOB_STATUS_SKIN: Record<string, WtSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  scheduled: { label: 'Scheduled', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  rigging_up: { label: 'Rigging up', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  flowing: { label: 'Flowing', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  rigging_down: { label: 'Rigging down', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  completed: { label: 'Completed', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
const pickSkin = (map: Record<string, WtSkin>, v: string | null | undefined): WtSkin =>
  v && Object.prototype.hasOwnProperty.call(map, v) ? map[v] : WT_UNKNOWN_SKIN;
export const wtJobStatusSkin = (status: string | null | undefined): WtSkin => pickSkin(WT_JOB_STATUS_SKIN, status);

export const WT_RENT_STATUS_SKIN: Record<string, WtSkin> = {
  upcoming: { label: 'Upcoming', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  on_rent: { label: 'On rent', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  off_rent: { label: 'Off rent', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const wtRentStatusSkin = (status: string | null | undefined): WtSkin => pickSkin(WT_RENT_STATUS_SKIN, status);

/** Open = draft through rigging_down; takes edits. */
export const wtJobOpen = (status: string | null | undefined) => !!status && status !== 'completed' && status !== 'cancelled';
/** Loggable = scheduled through rigging_down (lib/wt-jobs.ts WT_LOGGABLE). */
export const wtJobLoggable = (status: string | null | undefined) =>
  status === 'scheduled' || status === 'rigging_up' || status === 'flowing' || status === 'rigging_down';
/** Only a draft or scheduled job can be deleted; the API decides the logs half. */
export const wtJobDeletable = (status: string | null | undefined) => status === 'draft' || status === 'scheduled';

const xstr = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);

/**
 * Every refusal code the WT-2 routes send, as one plain sentence. A code this
 * map lacks falls back to the API's own `error` string, then to `fallback`.
 */
const WT_REFUSALS: Record<string, (x: Record<string, unknown>) => string> = {
  invalid_transition: (x) => {
    const allowed = Array.isArray(x.allowed) ? (x.allowed as string[]) : [];
    return allowed.length
      ? `This job cannot move to that status. From here it can go to: ${allowed.map((a) => wtJobStatusSkin(a).label).join(', ')}.`
      : 'This job cannot move to that status from where it is.';
  },
  job_closed: () => 'This job is completed or cancelled, so it can no longer be changed.',
  job_not_deletable: () => 'Only a draft or scheduled job can be deleted. Cancel this one instead.',
  job_has_logs: () => 'This job has shift logs, so it cannot be deleted. Cancel it instead.',
  job_not_scheduled: () => 'This job is still a draft. Schedule it before logging a shift on it.',
  job_number_contended: () => 'Another job took that number at the same moment. Try again.',
  well_customer_mismatch: () => 'That well belongs to a different customer.',
  well_has_live_jobs: (x) =>
    `This well has an open job${xstr(x.jobNumber) ? ` (${xstr(x.jobNumber)})` : ''}, so it cannot be deleted. Complete or cancel the job first.`,
  already_on_crew: () => 'That person is already on the crew.',
  already_on_job: () => 'That unit is already on this job.',
  package_empty: () => 'That package has no units in it. Add units to the package first.',
  package_unit_unavailable: () => 'A unit in that package is retired or archived. Swap it out of the package first.',
  package_on_open_job: (x) =>
    `This package is on an open job${xstr(x.jobNumber) ? ` (${xstr(x.jobNumber)})` : ''}, so it cannot be deleted. Take it off the job or close the job first.`,
  unit_in_other_package: () => 'That unit is already in another package. Take it out of that package first.',
  duplicate_package: () => 'Another package already has that name.',
  spec_exists: () => 'That unit already has specs. Edit them instead.',
  unit_hours_outside_dates: (x) =>
    `This unit has hours logged on ${xstr(x.workDate) ? wtDay(xstr(x.workDate)) : 'a day'} outside the new dates. Keep that day inside them.`,
  unit_has_hours: () => 'This unit has hours on a shift log, so it cannot be taken off the job. Set its off date instead.',
  invalid_rent_dates: () => 'The off-rent date cannot be before the on-rent date.',
  rent_in_hours_outside_dates: (x) =>
    `This rented-in unit has hours logged on ${xstr(x.workDate) ? wtDay(xstr(x.workDate)) : 'a day'} outside the new rent dates. Keep that day inside them.`,
  rent_in_has_hours: () => 'This rented-in unit has hours on a shift log. Set its off-rent date instead of removing it.',
  future_day: (x) => `A shift log cannot be for a future day. Today is ${xstr(x.today) ? wtDay(xstr(x.today)) : 'earlier'}.`,
  shift_log_exists: () => 'This job already has a log for that day and shift. Open that log instead.',
  user_not_on_crew: () => 'Only people on the job\'s crew can be marked present. Add them to the crew first.',
  equipment_not_on_job: () => 'That unit is not on this job. Add it to the job\'s equipment first.',
  rent_in_not_on_job: () => 'That rented-in unit belongs to another job.',
  rent_in_not_on_rent: () => 'That rented-in unit was not on rent that day.',
  meter_owned_only: () => 'An hour-meter reading can only be recorded for an owned unit.',
  duplicate_crew: () => 'The same person is listed twice. Remove one.',
  duplicate_unit: () => 'The same unit is listed twice. Remove one of the rows.',
  not_on_crew: () => 'You are not on this job\'s crew, so you cannot open it or log a shift on it.',
  not_found: () => 'That record no longer exists. It may have been deleted.',
  // ─── WT-3 (fieldiq-api#1248) ───────────────────────────────────────────
  future_read_at: () => 'That reading time is in the future. Check the time, and the phone\'s clock.',
  tank_not_on_job: () => 'That tank is not on this job. Add it to the job\'s equipment first.',
  duplicate_tank: () => 'The same tank is listed twice. Remove one of the gauges.',
  empty_reading: () => 'Enter at least one value or tank gauge before saving.',
  reading_exists: () => 'This job already has a reading at that time. Pick another time, or correct that reading.',
  reading_superseded: () => 'This reading has already been corrected. Correct the newer one instead.',
  period_overlap: () => 'That start overlaps an existing test period. Start the new period after the last one began.',
  period_has_later_readings: () => 'The open period has readings after that time. Pick a time after its last reading.',
  period_open: () => 'Another test period is open on this job. End it first.',
  flag_acknowledged: () => 'This flag was already acknowledged by someone else.',
  report_exists: () => 'This job already has a draft report. Open that one instead of starting another.',
  report_issued: () => 'This report is issued and can no longer be changed. Start a new draft for a revision.',
  job_not_completed: () => 'The end-of-job report can only be issued once the job is completed. Complete the job first.',
  already_issued: (x) => `This report is already issued${xstr(x.reportNumber) ? ` as ${xstr(x.reportNumber)}` : ''}.`,
  job_cancelled: () => 'This job is cancelled, so it gets no end-of-job report.',
  report_changed: () => 'The report changed while it was being issued. Try again.',
  signatory_unknown: () => 'Your user account has no name to sign the report with. Add your name to your profile first.',
};

export type WtRefusalOverrides = Record<string, (x: Record<string, unknown>) => string>;

/** The shift-log wording of these codes, said about a reading instead. */
export const WT_READING_REFUSALS: WtRefusalOverrides = {
  job_not_scheduled: () => 'This job is still a draft. Schedule it before entering readings.',
  job_closed: () => 'This job is completed or cancelled, so it takes no more readings.',
  not_on_crew: () => 'You are not on this job\'s crew, so you cannot enter a reading on it.',
};

/** …said about a day's flared / vented / conserved volumes. */
export const WT_VOLUME_REFUSALS: WtRefusalOverrides = {
  job_not_scheduled: () => 'This job is still a draft, so it has no daily volumes yet.',
  job_closed: () => 'This job is cancelled, so its daily volumes cannot be changed.',
  future_day: (x) => `Volumes cannot be entered for a future day. Today is ${xstr(x.today) ? wtDay(xstr(x.today)) : 'earlier'}.`,
};

/** …said about the end-of-job report. */
export const WT_REPORT_REFUSALS: WtRefusalOverrides = {
  job_not_scheduled: () => 'This job is still a draft, so it has nothing to report yet.',
};

/** The codes `wtRefusal` has a sentence for — the test checks each reads as a sentence. */
export const WT_REFUSAL_CODES = Object.keys(WT_REFUSALS);

/**
 * A WT-2 write's (or read's) error as one plain sentence. A coded refusal
 * reads from the map; a bare 404 reads as gone; a coded 403 (not_on_crew)
 * keeps its sentence, any other 403 is a permission refusal; anything else
 * (invalid_request included) is the API's own message.
 */
export function wtRefusal(err: unknown, fallback: string, overrides?: WtRefusalOverrides): string {
  const r = (err as { response?: { status?: unknown; data?: unknown } })?.response;
  const d = (r?.data && typeof r.data === 'object' ? r.data : {}) as Record<string, unknown>;
  const status = typeof r?.status === 'number' ? r.status : null;
  const code = typeof d.code === 'string' ? d.code : null;
  if (code && overrides?.[code]) return overrides[code](d);
  if (code && WT_REFUSALS[code]) return WT_REFUSALS[code](d);
  if (status === 404) return WT_REFUSALS.not_found({});
  if (status === 403) return 'You do not have permission to do this. Ask an admin or office staff.';
  return (typeof d.error === 'string' && d.error) || fallback;
}

export const wtErrorCode = (err: unknown): string | null => {
  const c = (err as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof c === 'string' ? c : null;
};

/** A save warning as one sentence (office camelCase or field snake_case). */
export function wtWarningText(
  w: { code: string; entered?: number; previous?: number; previousDate?: string; previous_date?: string },
  unitName?: string | null,
): string {
  if (w.code === 'meter_below_previous') {
    const day = w.previousDate ?? w.previous_date;
    return `${unitName ?? 'A unit'}: hour meter ${fmtWtNumber(w.entered)} is below the last reading of ${fmtWtNumber(w.previous)}${
      day ? ` on ${wtDay(day)}` : ''}. Check the meter; the log was saved.`;
  }
  return `Saved with a warning (${w.code}).`;
}

// ═══ WT-3 (fieldiq-api#1248): readings, flags, daily volumes, reports ═══════

/** A reading field's write key (snake_case) as the office reading's `values` key (camelCase). */
export const wtCamelKey = (key: string): string => key.replace(/_([a-z0-9])/g, (_, m: string) => m.toUpperCase());

/** A figure the API converted for display, with its unit: `412.5 mcf`. Absent → `—`. */
export function wtShown(v: { display: number | null; displayUnit: string } | null | undefined): string {
  if (!v) return '—';
  return withUnit(v.display, v.displayUnit);
}

/** The entry units a reading field offers: the kind's accepted units (catalog), plus the tenant default. Unitless → none. */
export function wtFieldUnits(
  field: { kind: string; defaultUnit: string },
  quantityUnits: Record<string, { metric: string; accepted: string[] }> | null | undefined,
): string[] {
  if (field.kind === 'unitless') return [];
  const accepted = quantityUnits?.[field.kind]?.accepted ?? [];
  return accepted.includes(field.defaultUnit) ? accepted : [field.defaultUnit, ...accepted];
}

/** How a unit reads in a picker. */
export const wtUnitLabel = (unit: string): string =>
  ({ ft_in: 'ft & in', e3m3: 'e³m³', 'e3m3/d': 'e³m³/d', m3: 'm³', 'm3/d': 'm³/d', C: '°C', F: '°F' } as Record<string, string>)[unit] ?? unit;

/** The phone and office reading form's groups, in the order a hand reads the package. */
export const WT_READING_GROUPS: { key: string; label: string; fields: string[] }[] = [
  { key: 'pressures', label: 'Pressures', fields: ['wellhead_pressure', 'casing_pressure', 'tubing_pressure', 'separator_pressure', 'separator_temperature'] },
  { key: 'meter', label: 'Choke & meter', fields: ['choke_size', 'orifice_plate', 'differential_pressure'] },
  { key: 'rates', label: 'Rates', fields: ['oil_rate', 'condensate_rate', 'water_rate', 'gas_rate', 'gas_flared_rate', 'gas_vented_rate', 'gas_conserved_rate'] },
  { key: 'cumulatives', label: 'Cumulatives', fields: ['cumulative_oil', 'cumulative_water', 'cumulative_gas', 'load_recovered'] },
  { key: 'sand', label: 'Sand dump', fields: ['sand_per_dump'] },
  { key: 'fluids', label: 'H2S, CO2 & BS&W', fields: ['h2s', 'co2', 'bsw'] },
];

/** A field the API serves that no group names still gets a place (last), never dropped. */
export function wtGroupFields<F extends { key: string }>(fields: F[]): { key: string; label: string; fields: F[] }[] {
  const byKey = new Map(fields.map((f) => [f.key, f]));
  const placed = new Set<string>();
  const out = WT_READING_GROUPS.map((g) => {
    const fs = g.fields.map((k) => byKey.get(k)).filter((f): f is F => !!f);
    fs.forEach((f) => placed.add(f.key));
    return { key: g.key, label: g.label, fields: fs };
  }).filter((g) => g.fields.length > 0);
  const rest = fields.filter((f) => !placed.has(f.key));
  if (rest.length) out.push({ key: 'other', label: 'Other', fields: rest });
  return out;
}

export interface WtReadingEntry {
  value: string;
  unit: string;
}

/**
 * The form's state as a write body: each non-blank field as typed plus its
 * unit (a unitless field sends no unit), and each tank gauge typed. Nothing
 * is converted — the API keeps the value as typed and computes the metric copy.
 */
export function wtReadingBody(
  fields: { key: string; kind: string }[],
  entries: Record<string, WtReadingEntry>,
  tanks: { equipmentId: string; level: string; unit: string }[],
  extra: { readAt?: string; notes?: string },
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  for (const f of fields) {
    const e = entries[f.key];
    if (!e || e.value.trim() === '') continue;
    body[f.key] = e.value.trim();
    if (f.kind !== 'unitless' && e.unit) body[`${f.key}_unit`] = e.unit;
  }
  const gauged = tanks.filter((t) => t.level.trim() !== '');
  if (gauged.length) body.tanks = gauged.map((t) => ({ equipment_id: t.equipmentId, level: t.level.trim(), level_unit: t.unit }));
  if (extra.readAt) body.read_at = extra.readAt;
  if (extra.notes && extra.notes.trim()) body.notes = extra.notes.trim();
  return body;
}

/** One flag as a sentence, from the API's own numbers. `metricUnit` names a cumulative's metric copy. */
export function wtFlagText(
  f: { kind: string; fieldLabel: string; value: number; threshold: number },
  metricUnit?: string | null,
): string {
  const n = (v: number) => fmtWtNumber(v);
  switch (f.kind) {
    case 'pressure_drop':
      return `${f.fieldLabel} fell ${n(f.value)}% from the previous reading (limit ${n(f.threshold)}%).`;
    case 'rate_change':
      return `${f.fieldLabel} moved ${n(f.value)}% from the previous reading (limit ${n(f.threshold)}%).`;
    case 'h2s_over_limit':
      return `H2S read ${n(f.value)} ppm, over the ${n(f.threshold)} ppm action limit.`;
    case 'missed_interval':
      return `${n(f.value)} minutes since the previous reading (allowed ${n(f.threshold)} minutes).`;
    case 'cumulative_decrease': {
      const u = metricUnit ? ` ${wtUnitLabel(metricUnit)}` : '';
      return `${f.fieldLabel} read ${n(f.value)}${u}, below the previous reading's ${n(f.threshold)}${u}.`;
    }
    default:
      return `${f.fieldLabel}: ${n(f.value)} (threshold ${n(f.threshold)}).`;
  }
}

export const WT_REPORT_STATUS_SKIN: Record<string, WtSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  issued: { label: 'Issued', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
};
export const wtReportStatusSkin = (status: string | null | undefined): WtSkin => pickSkin(WT_REPORT_STATUS_SKIN, status);

/**
 * Minutes until the next reading is due (negative once overdue), from the
 * API's `next_due_at` and a clock. null when nothing is due yet.
 */
export function wtMinutesUntil(nextDueAt: string | null | undefined, now: number): number | null {
  if (!nextDueAt) return null;
  const t = Date.parse(nextDueAt);
  if (!Number.isFinite(t)) return null;
  return Math.round((t - now) / 60000);
}

/** The countdown as words: `Next reading due in 1 h 05 min` / `Overdue by 12 min`. */
export function wtDueText(mins: number | null): string {
  if (mins === null) return 'No reading yet — this will be the first.';
  const a = Math.abs(mins);
  const span = a >= 60 ? `${Math.floor(a / 60)} h ${String(a % 60).padStart(2, '0')} min` : `${a} min`;
  if (mins > 0) return `Next reading due in ${span}`;
  if (mins === 0) return 'Next reading due now';
  return `Reading overdue by ${span}`;
}
